import { useRef, useState } from "react";
import { ErrorMessage, Field, Form, Formik } from "formik";
import { toast } from "react-toastify";
import { countries } from "country-list-json";
import { ReCAPTCHA } from "@/lib/vendor";
import * as yup from "yup";
import apiService from "@/common/services/apiService";
import { useRouter } from "next/router";
import { hideFormLoader, showFormLoader } from "@/store/loaderSlice";
import { useDispatch } from "react-redux";

interface Props {
  show: boolean;
  handleClose: () => void;
}

const GetResourceRateModal = ({ show, handleClose }: Props) => {
  const router = useRouter();
  const dispatch = useDispatch();
  const sitekey = process.env.NEXT_PUBLIC_RECAPTCHA_V3 ?? "";
  const reCaptchaRef = useRef<ReCAPTCHA>(null);
  const [captchaError, setCaptchaError] = useState("");

  const getTheme =
    typeof window !== "undefined" && localStorage.getItem("Theme") === "dark"
      ? "dark"
      : "light";

  const validateCaptchaV3 = (): Promise<string | null> => {
    return new Promise((resolve) => {
      reCaptchaRef.current?.executeAsync()?.then((value) => {
        resolve(value ?? null);
      });
    });
  };

  const handleSubmit = async (formData: any, actions: any) => {
    const isHuman = await validateCaptchaV3();
    if (!isHuman) {
      setCaptchaError("Invalid captcha.");
      return;
    }

    dispatch(showFormLoader());
    setCaptchaError("");
    const payload = {
      ...formData,
      captcha_token_v3: isHuman,
      page_link: window.location.href,
      page_title: document.title,
    };

    const res:any = await apiService.post("resource-rate", { data: payload });

    dispatch(hideFormLoader());
    actions.resetForm();
    reCaptchaRef.current?.reset();

    if (res.data.success) {
      router.push("/thank-you");
      handleClose();
    } else {
      toast.error(res.data.message);
    }
  };

  if (!show) return null;

  return (
    <div className="custom-modal-overlay">
      <div className="custom-modal">
        <button className="close-btn" onClick={handleClose}>
          ✕
        </button>

        <h2 className="title">Get Resource Rate</h2>

        <Formik
          initialValues={{
            name: "",
            business_email: "",
            company_name: "",
            designation: "",
            country_name: "",
            message: "",
          }}
          validationSchema={yup.object({
            name: yup.string().required("Name is required."),
            business_email: yup
              .string()
              .email("Invalid Email.")
              .required("Email is required."),
            company_name: yup.string().required("Company Name is required."),
            designation: yup.string().required("Designation is required."),
            country_name: yup.string().required("Country Name is required."),
            message: yup.string().required("Message is required."),
          })}
          onSubmit={handleSubmit}
        >
          {() => (
            <Form className="modal-form">
              <div className="grid-2">
                <div>
                  <label>Name *</label>
                  <Field name="name" className="input" />
                  <ErrorMessage
                    name="name"
                    component="span"
                    className="error"
                  />
                </div>
                <div>
                  <label>Email *</label>
                  <Field name="business_email" type="email" className="input" />
                  <ErrorMessage
                    name="business_email"
                    component="span"
                    className="error"
                  />
                </div>
                <div>
                  <label>Company *</label>
                  <Field name="company_name" className="input" />
                  <ErrorMessage
                    name="company_name"
                    component="span"
                    className="error"
                  />
                </div>
                <div>
                  <label>Designation *</label>
                  <Field name="designation" className="input" />
                  <ErrorMessage
                    name="designation"
                    component="span"
                    className="error"
                  />
                </div>
                <div>
                  <label>Country *</label>
                  <Field as="select" name="country_name" className="select">
                    <option value="">Select Country</option>
                    {countries.map((c) => (
                      <option key={c.code} value={c.name}>
                        {c.name}
                      </option>
                    ))}
                  </Field>
                  <ErrorMessage
                    name="country_name"
                    component="span"
                    className="error"
                  />
                </div>
              </div>

              <div>
                <label>Message *</label>
                <Field
                  as="textarea"
                  name="message"
                  rows={3}
                  className="textarea"
                />
                <ErrorMessage
                  name="message"
                  component="span"
                  className="error"
                />
              </div>

              <ReCAPTCHA
                style={{ display: "inline" }}
                theme={getTheme}
                size="invisible"
                sitekey={sitekey}
                ref={reCaptchaRef}
              />
              <span className="error">{captchaError}</span>

              <div className="actions">
                <button
                  type="button"
                  className="btn secondary"
                  onClick={handleClose}
                >
                  Close
                </button>
                <button type="submit" className="btn primary">
                  Submit
                </button>
              </div>
            </Form>
          )}
        </Formik>
      </div>
    </div>
  );
};

export default GetResourceRateModal;
